import React, { useCallback, useMemo, useEffect } from "react";
import { FormControl, Modal } from "react-bootstrap";
import { CoiContact } from "../../models";
import classNames from "classnames";
import { ContactInput } from "./contact-input";

type ContactErrorField =
  | "primaryContactError"
  | "additionalContact1Error"
  | "additionalContact2Error"
  | "additionalContact3Error"
  | "additionalContact4Error";

function CoiContactFormModel(props: {
  isShowContactEditModal: boolean;
  SetIsShowContactEditModal: (x: boolean) => void;
  coiContact: CoiContact;
  originalCoiContact: CoiContact;
  onSubmit: any;
  setCoiContact: (x: any) => void;
  submittingContacts: boolean;
  subHeader: string;
  loadingContacts: boolean;
}) {
  const {
    isShowContactEditModal,
    SetIsShowContactEditModal,
    coiContact,
    onSubmit,
    setCoiContact,
    submittingContacts,
    subHeader,
    originalCoiContact,
    loadingContacts,
  } = props;

  const hasContactChanges = useCallback(() => {
    return (
      coiContact.primaryContact !== originalCoiContact.primaryContact ||
      coiContact.additionalContact1 !== originalCoiContact.additionalContact1 ||
      coiContact.additionalContact2 !== originalCoiContact.additionalContact2 ||
      coiContact.additionalContact3 !== originalCoiContact.additionalContact3 ||
      coiContact.additionalContact4 !== originalCoiContact.additionalContact4
    );
  }, [coiContact, originalCoiContact]);

  const isValidEmail = (email: string): boolean => {
    return /^([a-zA-Z0-9_.+-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/.test(
      email
    );
  };

  const submitContacts = useCallback(() => {
    const contacts: {
      value: string;
      errorField: ContactErrorField;
    }[] = [
      {
        value: coiContact.primaryContact,
        errorField: "primaryContactError",
      },
      {
        value: coiContact.additionalContact1,
        errorField: "additionalContact1Error",
      },
      {
        value: coiContact.additionalContact2,
        errorField: "additionalContact2Error",
      },
      {
        value: coiContact.additionalContact3,
        errorField: "additionalContact3Error",
      },
      {
        value: coiContact.additionalContact4,
        errorField: "additionalContact4Error",
      },
    ];

    const errors: Partial<Record<ContactErrorField, string>> = {};

    // Primary Contact is required
    if (!coiContact.primaryContact?.trim()) {
      errors.primaryContactError = "Primary Contact is required.";
    }

    // Validate email format
    contacts.forEach((contact) => {
      const email = contact.value?.trim();

     
      if (email && !isValidEmail(email)) {
        errors[contact.errorField] = "Please enter a valid email address.";
      }else  if (email?.length > 70) {
        errors[contact.errorField] =
          "Email address cannot exceed 70 characters.";
      }

    });

    

    // Find duplicate emails
    const emailMap = new Map<string, ContactErrorField[]>();

    contacts.forEach((contact) => {
      const email = contact.value?.trim().toLowerCase();

      if (!email) {
        return;
      }

      if (!emailMap.has(email)) {
        emailMap.set(email, []);
      }

      emailMap.get(email)!.push(contact.errorField);
    });

    // Add duplicate errors
    emailMap.forEach((errorFields) => {
      if (errorFields.length > 1) {
        errorFields.forEach((errorField) => {
          errors[errorField] = "Cannot add the same contact more than once.";
        });
      }
    });

    // Clear old errors and apply new errors
    const updatedContact: CoiContact = {
      ...coiContact,

      primaryContactError: "",
      additionalContact1Error: "",
      additionalContact2Error: "",
      additionalContact3Error: "",
      additionalContact4Error: "",

      ...errors,
    };

    setCoiContact(updatedContact);

    // Don't submit if there are validation errors
    if (Object.keys(errors).length > 0) {
      return;
    }

    onSubmit(coiContact);
  }, [coiContact, onSubmit, setCoiContact]);

  return (
    <Modal
      show={isShowContactEditModal}
      keyboard={false}
      animation={false}
      dialogClassName="Delete-Commitment"
      backdrop="static"
      centered
    >
      <Modal.Header
        style={{
          fontSize: 19,
          paddingTop: "12px",
          paddingRight: "24px",
          paddingBottom: "12px",
          paddingLeft: "24px",
          borderRadius: "4px 4px 0px 0px",
          borderBottom: "none",
          fontWeight: "bold",
        }}
      >
        <div>Edit COI Contacts</div>
      </Modal.Header>
      <Modal.Body className="px-4">
        <p className="header-details">
          Each record must always have 1 primary contact. You may add up to 4
          additional contacts.
          {/*<br />
          Contact options on this screen come from the Procore directory. Don’t
          see the person you need? Add them to the Procore directory first.*/}
        </p>

        { loadingContacts ?
      (<div className="contact-input animate-pulse" />)
    :
        (<p  style={{
          fontSize: 14,
          paddingTop: "2px",
          paddingBottom: "1px",
          fontWeight: "bold",
        }}>{subHeader}</p>)}

       
        <div className="contact-field">
          <div className="contact-icon">
            <i className="fa fa-user"></i>
          </div>

          <div className="contact-content">
            <label className="contact-label">Primary Contact <span className="required-field">*</span></label>
            <ContactInput
              value={coiContact.primaryContact}
              error={coiContact.primaryContactError}
              loadingContacts={loadingContacts}
              onChange={(value) =>
                setCoiContact({
                  ...coiContact,
                  primaryContact: value,
                  primaryContactError: "",
                })
              }
            />
            
          </div>
          <div className="contact-content mt-2">
            <label className="contact-label">Additional Contact 1</label>

            <ContactInput
              value={coiContact.additionalContact1}
              error={coiContact.additionalContact1Error}
              loadingContacts={loadingContacts}
              onChange={(value) =>
                setCoiContact({
                  ...coiContact,
                  additionalContact1: value,
                  additionalContact1Error: "",
                })
              }
            />
           
          </div>
          <div className="contact-content mt-2">
            <label className="contact-label">Additional Contact 2</label>

            <ContactInput
              value={coiContact.additionalContact2}
              error={coiContact.additionalContact2Error}
              loadingContacts={loadingContacts}
              onChange={(value) =>
                setCoiContact({
                  ...coiContact,
                  additionalContact2: value,
                  additionalContact2Error: "",
                })
              }
            />
            
          </div>
          <div className="contact-content mt-2">
            <label className="contact-label">Add Additional Contact 3</label>

            <ContactInput
              value={coiContact.additionalContact3}
              error={coiContact.additionalContact3Error}
              loadingContacts={loadingContacts}
              onChange={(value) =>
                setCoiContact({
                  ...coiContact,
                  additionalContact3: value,
                  additionalContact3Error: "",
                })
              }
            />
            
          </div>
          <div className="contact-content mt-2">
            <label className="contact-label">Add Additional Contact 4</label>

            <ContactInput
              value={coiContact.additionalContact4}
              error={coiContact.additionalContact4Error}
              loadingContacts={loadingContacts}
              onChange={(value) =>
                setCoiContact({
                  ...coiContact,
                  additionalContact4: value,
                  additionalContact4Error: "",
                })
              }
            />
            
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div>
          <button
            className="contact-cancel-btn"
            onClick={() => {
              SetIsShowContactEditModal(false);
              return;
            }}
          >
            Cancel
          </button>
        </div>
        <div style={{ paddingRight: "inherit" }}>
          <button
            className="add-contact-btn"
            onClick={submitContacts}
            disabled={submittingContacts || !hasContactChanges()}
          >
            {submittingContacts ? "Submitting..." : "Save Changes"}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default CoiContactFormModel;
